import React, { useMemo } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { PlayIcon } from '@vlabs/icons';
import { TableCellsWithPermissions } from '@vlabs/shared/components/table/cells/index';
import { TableBottomPanelWithPermissions } from '@vlabs/shared/components/table/TableBottomPanelWithPermissions';
import LoadingPage from '@vlabs/shared/components/loading-page/LoadingPage';
import { permissions } from '@vlabs/shared/config';
import { useApiRequest, usePagination } from '@vlabs/shared/hooks';
import { routes } from '@vlabs/shared/navigation/routes';
import { actionColumnProps } from '@vlabs/shared/utils';
import { Control, Panel, Table, openConfirmPopup } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';

const VerifyCell = ({ row }) => {
  const history = useHistory();
  const { t } = useTranslation();

  return (
    <Control.IconButton
      data-testid="linkCell"
      icon={<PlayIcon />}
      kind="primary"
      onClick={() => history.push(generatePath(routes.verifiers.verify, { verifierId: row.original.verifierId }))}
      title={t('verifiers:table.buttons.test_verifier')}
      variant="dimmed"
    />
  );
};

function VerifierListPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { pageIndex, pageSize, pageSizeOptions, setPageIndex, setPageSize } = usePagination();
  const { state: verifiers, isLoading, fetchData: fetchVerifiers } = useApiRequest({
    apiRequest: apiContainer.lunaClient.verifiers.getPage,
    parameters: useMemo(() => ({ page_size: pageSize, page: pageIndex + 1 }), [pageIndex, pageSize]),
  });

  const onDeleteVerifier = (verifier) => {
    const message = <span>{t('verifiers:table.confirmation.delete.text', { verifier })}</span>;

    const onConfirm = async () => {
      await apiContainer.lunaClient.verifiers.delete(verifier.verifierId);
      fetchVerifiers();
      toast.success(`${t('verifiers:api.delete_success')}.`);
    };

    openConfirmPopup({
      title: t('verifiers:table.confirmation.delete.title'),
      message,
      type: 'delete',
      onConfirm,
    });
  };

  const onEditVerifier = ({ verifierId }) => history.push(generatePath(routes.verifiers.read, { verifierId }));

  const onCreateVerifier = () => history.push(generatePath(routes.verifiers.create));

  const columns = useMemo(() => ([
    { Header: t('verifiers:table.columns.description'), accessor: 'description' },
    { Header: t('verifiers:table.columns.verifier_id'), accessor: 'verifierId' },
    actionColumnProps({ id: 'verify', Cell: VerifyCell }),
    actionColumnProps({ id: 'edit', Cell: TableCellsWithPermissions.EditCell }),
    actionColumnProps({ id: 'delete', Cell: TableCellsWithPermissions.DeleteCell }),
  ]
  ), [t]);

  if (isLoading || !verifiers.data) return <LoadingPage />;

  return (
    <Page>
      <PageContent>
      <Panel className="Global__Table_fullScreen PanelAutoHeight">
        <Table
          columns={columns}
          CustomBottomPanel={TableBottomPanelWithPermissions}
          data={verifiers.data}
          meta={verifiers.meta}
          onActions={{
            onAddRow: { handler: onCreateVerifier, permissions: { rules: [permissions.verifier.creation] } },
            onEditRow: { handler: onEditVerifier },
            onDeleteRow: { handler: onDeleteVerifier, permissions: { rules: [permissions.verifier.deletion] } },
          }}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          paginationType="offsetBased"
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
      </Panel>
      </PageContent>
    </Page>
  );
}

export default VerifierListPage;
